import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const INCOME_RANGES = [
  { value: 'under_25k', label: 'Under $25k' },
  { value: '25k_50k', label: '$25k – $50k' },
  { value: '50k_100k', label: '$50k – $100k' },
  { value: '100k_200k', label: '$100k – $200k' },
  { value: '200k_plus', label: '$200k+' },
];

const EMPLOYMENT_TYPES = [
  { value: 'employed', label: 'Employed', icon: 'Briefcase' },
  { value: 'self_employed', label: 'Self-Employed', icon: 'Building2' },
  { value: 'freelancer', label: 'Freelancer', icon: 'Laptop' },
  { value: 'student', label: 'Student', icon: 'GraduationCap' },
  { value: 'retired', label: 'Retired', icon: 'Sunset' },
  { value: 'other', label: 'Other', icon: 'MoreHorizontal' },
];

const SPENDING_PERSONALITIES = [
  { value: 'saver', label: 'Saver', icon: 'PiggyBank', desc: 'Prioritizes saving' },
  { value: 'spender', label: 'Spender', icon: 'ShoppingBag', desc: 'Enjoys spending' },
  { value: 'investor', label: 'Investor', icon: 'TrendingUp', desc: 'Grows wealth' },
  { value: 'planner', label: 'Planner', icon: 'Calendar', desc: 'Plans ahead' },
  { value: 'impulsive', label: 'Impulsive', icon: 'Zap', desc: 'Spontaneous buys' },
  { value: 'balanced', label: 'Balanced', icon: 'Scale', desc: 'Mix of all' },
];

const getRiskColor = (val) => {
  if (val <= 3) return '#22C55E';
  if (val <= 6) return '#F59E0B';
  return '#EF4444';
};

const getRiskLabel = (val) => {
  if (val <= 3) return 'Conservative';
  if (val <= 6) return 'Moderate';
  return 'Aggressive';
};

export default function FinancialProfileTab({ onToast }) {
  const { userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showReOnboardModal, setShowReOnboardModal] = useState(false);
  const [form, setForm] = useState({
    income_range: '',
    employment_type: '',
    risk_tolerance_score: 5,
    spending_personality: [],
  });

  useEffect(() => {
    if (userProfile) {
      setForm({
        income_range: userProfile?.income_range || '',
        employment_type: userProfile?.employment_type || '',
        risk_tolerance_score: userProfile?.risk_tolerance_score || 5,
        spending_personality: userProfile?.spending_personality || [],
      });
    }
  }, [userProfile]);

  const togglePersonality = (val) => {
    setForm(prev => {
      const current = prev?.spending_personality || [];
      if (current?.includes(val)) return { ...prev, spending_personality: current?.filter(v => v !== val) };
      if (current?.length >= 2) return prev;
      return { ...prev, spending_personality: [...current, val] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await updateProfile(form);
      if (error) throw error;
      onToast?.({ type: 'success', message: 'Financial profile saved!' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const riskColor = getRiskColor(form?.risk_tolerance_score || 5);

  return (
    <div className="space-y-8">
      {/* Income Range */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="DollarSign" size={16} color="#00E5FF" />
          Annual Income Range
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {INCOME_RANGES?.map(({ value, label }) => {
            const active = form?.income_range === value;
            return (
              <button
                key={value}
                onClick={() => setForm(prev => ({ ...prev, income_range: value }))}
                className="px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: active ? 'rgba(0,229,255,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? '#00E5FF' : 'rgba(255,255,255,0.08)'}`,
                  color: active ? '#00E5FF' : '#94A3B8',
                  boxShadow: active ? '0 0 12px rgba(0,229,255,0.2)' : 'none',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Employment Type */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Briefcase" size={16} color="#00E5FF" />
          Employment Type
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EMPLOYMENT_TYPES?.map(({ value, label, icon }) => {
            const active = form?.employment_type === value;
            return (
              <button
                key={value}
                onClick={() => setForm(prev => ({ ...prev, employment_type: value }))}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: active ? 'rgba(123,47,190,0.2)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? '#7B2FBE' : 'rgba(255,255,255,0.08)'}`,
                  color: active ? '#C084FC' : '#94A3B8',
                  boxShadow: active ? '0 0 12px rgba(123,47,190,0.25)' : 'none',
                }}
              >
                <Icon name={icon} size={15} color={active ? '#C084FC' : '#64748B'} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Risk Tolerance */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Activity" size={16} color="#00E5FF" />
          Financial Risk Tolerance
        </h3>
        <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,229,255,0.1)' }}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm" style={{ color: '#94A3B8' }}>Score: <span className="font-bold text-white">{form?.risk_tolerance_score || 5}</span>/10</span>
            <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ background: `${riskColor}22`, color: riskColor }}>
              {getRiskLabel(form?.risk_tolerance_score || 5)}
            </span>
          </div>
          <input
            type="range"
            min="1" max="10"
            value={form?.risk_tolerance_score || 5}
            onChange={e => setForm(prev => ({ ...prev, risk_tolerance_score: parseInt(e?.target?.value) }))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: riskColor }}
          />
          <div className="flex justify-between mt-2">
            {['Conservative', '', '', '', 'Moderate', '', '', '', '', 'Aggressive']?.map((label, i) => (
              <span key={i} className="text-xs" style={{ color: label ? '#64748B' : 'transparent' }}>{label || '|'}</span>
            ))}
          </div>
        </div>
      </div>
      {/* Spending Personality */}
      <div>
        <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
          <Icon name="User" size={16} color="#00E5FF" />
          Spending Personality
        </h3>
        <p className="text-xs mb-4" style={{ color: '#64748B' }}>Select up to 2 that best describe you</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SPENDING_PERSONALITIES?.map(({ value, label, icon, desc }) => {
            const active = form?.spending_personality?.includes(value);
            const disabled = !active && (form?.spending_personality?.length || 0) >= 2;
            return (
              <button
                key={value}
                onClick={() => !disabled && togglePersonality(value)}
                disabled={disabled}
                className="flex flex-col items-start gap-1 px-4 py-3 rounded-xl text-sm transition-all duration-200 hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: active ? 'rgba(0,229,255,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? '#00E5FF' : 'rgba(255,255,255,0.08)'}`,
                  boxShadow: active ? '0 0 12px rgba(0,229,255,0.15)' : 'none',
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon name={icon} size={14} color={active ? '#00E5FF' : '#64748B'} />
                  <span className="font-medium" style={{ color: active ? '#00E5FF' : '#94A3B8' }}>{label}</span>
                </div>
                <span className="text-xs" style={{ color: '#475569' }}>{desc}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <button
          onClick={() => setShowReOnboardModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-[1.02]"
          style={{ background: 'rgba(123,47,190,0.15)', border: '1px solid rgba(123,47,190,0.3)', color: '#C084FC' }}
        >
          <Icon name="RefreshCw" size={14} color="#C084FC" />
          Re-run Financial Onboarding
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 hover:scale-[1.02]"
          style={{ background: 'linear-gradient(135deg, #00E5FF, #0891B2)', color: '#0D1B2A', boxShadow: '0 0 20px rgba(0,229,255,0.3)' }}
        >
          {saving ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={15} color="currentColor" />}
          {saving ? 'Saving...' : 'Save Financial Profile'}
        </button>
      </div>
      {/* Re-onboard Modal */}
      {showReOnboardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-md p-6 rounded-2xl" style={{ background: '#0D1B2A', border: '1px solid rgba(123,47,190,0.3)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(123,47,190,0.2)' }}>
                <Icon name="RefreshCw" size={18} color="#C084FC" />
              </div>
              <h3 className="text-lg font-semibold text-white">Re-run Onboarding?</h3>
            </div>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>This will take you back to the financial onboarding flow to update your profile from scratch. Your existing data will be preserved.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowReOnboardModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.06)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)' }}>Cancel</button>
              <button onClick={() => navigate('/onboarding-dashboard')} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #7B2FBE, #9333EA)', color: 'white' }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
